import React, { createContext, useContext, useEffect, useState } from 'react';
import { bool, shape, number, arrayOf, object, node } from 'prop-types';
import { TextStyled } from '@nike/nike-design-system-components';
import { ProfileStroke } from '@nike/nike-design-system-icons';
import clsx from 'clsx';
import useBodega from '../../utils/hooks/bodegaHook';
import Link from '../Link';
import {
  StyledLi,
  desktopList,
  desktopLinkClass,
  verticalLine,
  StyledNav,
  iconBtnClass,
  desktopUserItemDropdownPopover,
  interactiveProfileElement,
  profileLink,
  hoveredLink,
  profileImageClass,
} from './styles';
import { ariaLabelForProfileIcon } from '../../../mockData';
import useMenuData from '../../utils/hooks/cmsDataHook';
import useFeatureFlags from '../../utils/hooks/featureFlagsHook';
import {
  MENU_TYPES,
  DATA_TYPES,
  GLOBAL_NAV_FIND_A_STORE,
  BODEGA_DEFAULTS,
  MODIFIER_IS_OPEN,
  PULSE_INSIGHTS,
} from '../../constants';
import useLocale from '../../utils/hooks/localeHook';
import { getLocalizedUrl, MembershipUrl } from '../../utils/url';
import useIdentity from '../../utils/hooks/identityHook';
import AnalyticsEventTypes from '../../utils/analyticsEvent';
import useGetUserGreeting from '../../utils/hooks/getUserGreetingHook';
import LinkColumn from '../LinkColumn';
import DropDownMenu from '../DropDownMenu';

export const ItemContext = createContext(null);
const LiContainer = props => {
  const { itemsLength, itemNdx, item } = useContext(ItemContext);
  return (
    <StyledLi {...props}>
      {/* eslint-disable-next-line react/prop-types */}
      {props.children}
      {itemNdx < itemsLength - 1 && (
        <div className={verticalLine} style={item.hasDropDown && { marginLeft: '0px' }} />
      )}
    </StyledLi>
  );
};

const ListItem = props => {
  const locale = useLocale();
  const { isLoggedIn: userIsLoggedIn } = useIdentity();
  const { item, itemsLength, itemNdx } = props;
  /**
   * we're using separate state to control the details state and the style state
   * if we control both styles and details with the same state the transitions stop working
   * in order for there to be a transition we need to render the content with closed styles
   * then we need to change the styles to show the content
   */
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const dropDownItems = item?.dropDownItems;
  const analyticsPayload = {
    props: { path: item?.path, hashKey: item?.hashKey },
    type: item?.analyticsEventName,
  };

  const profileImage =
    item.isLogin &&
    userIsLoggedIn &&
    (item.avatarUrl ? (
      <img className={profileImageClass} src={item.avatarUrl} />
    ) : (
      <ProfileStroke aria-label={ariaLabelForProfileIcon} className={iconBtnClass} />
    ));

  // Use the url if supplied, or create a localized register link if it's a login item.
  // Sign in behavior is actually managed by onSignInClick and not the href.
  // /register is for accessibility. See https://jira.nike.com/browse/ALLY-7179 for more info.
  const href = item?.url || (item.isLogin && getLocalizedUrl('register')(locale));

  const onSignInClick = event => {
    event.preventDefault();
    window.webShellClient.identity.signIn().finally();
  };

  const detailsContentClass = clsx(desktopUserItemDropdownPopover, showContent && MODIFIER_IS_OPEN);

  useEffect(() => {
    setShowContent(isMenuOpen);
  }, [isMenuOpen]);

  return (
    <ItemContext.Provider
      value={{
        item,
        itemNdx,
        itemsLength,
      }}
    >
      <DropDownMenu
        MenuContainerComponent={LiContainer}
        containerClassName={desktopLinkClass}
        detailsContentCustomClass={detailsContentClass}
        isMenuOpen={isMenuOpen}
        menuLabel={item.menuLabel}
        menuLink={
          <Link
            className={clsx(
              interactiveProfileElement,
              item.isLogin && profileLink,
              isMenuOpen && hoveredLink,
            )}
            clickEventPayload={analyticsPayload}
            href={href}
            onClick={item.isLogin && !userIsLoggedIn ? onSignInClick : undefined}
          >
            <TextStyled
              appearance="body3Strong"
              data-testid={`desktop-user-menu-item-message-${itemNdx}`}
              role="presentation"
            >
              {item.msg}
            </TextStyled>
            {profileImage}
          </Link>
        }
        openMenuLabel={item.openMenuLabel}
        setMenuOpen={setMenuOpen}
        testId={'desktop-drop-down-styled-wrapper-div'}
      >
        {dropDownItems && <LinkColumn content={dropDownItems} />}
      </DropDownMenu>
    </ItemContext.Provider>
  );
};

const DesktopUtilityMenu = () => {
  const { isLoggedIn: userIsLoggedIn, userProfile } = useIdentity();
  const locale = useLocale();
  const helpMenu = useMenuData(DATA_TYPES.HEADER, MENU_TYPES.HELP_MENU)?.children?.[0];
  const { HELP, JOIN_US, FIND_A_STORE, SIGN_IN } = BODEGA_DEFAULTS;
  const storeLocatorUrl = getLocalizedUrl('retail');
  // control with feature flags
  const findStoreEnabled = useFeatureFlags(GLOBAL_NAV_FIND_A_STORE);
  const isSiteFeedbackEnabled = useFeatureFlags(PULSE_INSIGHTS);
  const userGreetingMsg = useGetUserGreeting();
  const { USER_NAVIGATION } = BODEGA_DEFAULTS;
  const userNavigationLabel = useBodega('nav_header_user_navigation', USER_NAVIGATION);

  const helpMenuData = helpMenu?.children?.map(menu => {
    return { url: menu.url, label: menu.label, path: menu.path, hashKey: menu.hashKey };
  });

  // Display site-feedback link in help menu based on flags
  const filteredHelpMenuData = isSiteFeedbackEnabled
    ? helpMenuData
    : helpMenuData.filter(menuItem => menuItem?.url !== '#site-feedback');

  const accountMenu = useMenuData(DATA_TYPES.HEADER, MENU_TYPES.ACCOUNT_SETTINGS_MENU)
    ?.children?.[0];
  const accountMenuLabel = useBodega('nav_header_label_account', accountMenu?.label);
  const accountSettingsData = accountMenu?.children?.map(menu => {
    return {
      url: menu?.url,
      label: menu?.label,
      path: menu?.path,
      analyticsEventName: AnalyticsEventTypes.CLICK_NAV_ACCOUNT,
      classification: menu?.classification,
      hashKey: menu?.hashKey,
    };
  });

  const accountSettingsDropDownItems = {
    content: accountSettingsData,
    title: accountMenuLabel,
    titleLink: accountMenu?.url,
    classification: accountMenu?.classification,
    path: accountMenu?.path,
    analyticsEventName: AnalyticsEventTypes.CLICK_NAV_ACCOUNT,
    hashKey: accountMenu?.hashKey,
  };

  const helpDropDownItems = {
    content: filteredHelpMenuData,
    title: helpMenu?.label,
    titleLink: helpMenu?.url,
    classification: accountMenu?.classification,
    path: helpMenu?.path,
    analyticsEventName: AnalyticsEventTypes.CLICK_HELP_MENU,
    hashKey: helpMenu?.hashKey,
  };

  const userMenuItems = [];
  const msgs = {
    clickNavStoreLocator: useBodega('nav_header_find_a_store', FIND_A_STORE),
    clickNavAccount: useBodega('nav_menu_aria_label_click_navAccount', 'Account Menu'),
    joinUs: useBodega('nav_title_joinus_membership', JOIN_US),
    signIn: useBodega('nav_title_signin_membership', SIGN_IN),
    help: useBodega('nav_header_label_help', HELP),
  };

  // only add store locator button in certain geos
  if (findStoreEnabled)
    userMenuItems.push({
      analyticsEventName: 'click_navStoreLocator',
      path: 'store:locator',
      msg: msgs.clickNavStoreLocator,
      url: storeLocatorUrl(locale),
    });

  if (helpMenu) {
    userMenuItems.push({
      analyticsEventName: 'click_navHelpMenu',
      path: 'get help',
      hasDropDown: true,
      menuLabel: msgs.help,
      msg: msgs.help,
      dropDownItems: helpDropDownItems,
      url: helpMenu?.url,
      hashKey: helpMenu?.hashKey,
    });
  }
  const { avatarUrl } = userProfile || {};
  if (userIsLoggedIn) {
    userMenuItems.push({
      analyticsEventName: AnalyticsEventTypes.CLICK_NAV_ACCOUNT,
      path: 'myAccount:greeting',
      hasDropDown: true,
      menuLabel: msgs.clickNavAccount,
      isLogin: true,
      msg: userGreetingMsg,
      dropDownItems: accountSettingsDropDownItems,
      avatarUrl,
      url: accountMenu?.url,
      hashKey: accountMenu?.hashKey,
    });
  } else {
    userMenuItems.push(
      {
        analyticsEventName: AnalyticsEventTypes.CLICK_NAV_MEMBERSHIP,
        path: 'join us',
        msg: msgs.joinUs,
        url: MembershipUrl(locale),
      },
      {
        analyticsEventName: AnalyticsEventTypes.CLICK_NAV_MEMBERSHIP,
        path: 'sign in',
        isLogin: true,
        msg: msgs.signIn,
      },
    );
  }

  const itemsLength = userMenuItems.length;

  return (
    <StyledNav aria-label={userNavigationLabel}>
      {/* removed Podium list because it's applying styles to all li under ul */}
      <ul className={desktopList}>
        {userMenuItems.map((item, ndx) => (
          <ListItem item={item} itemNdx={ndx} itemsLength={itemsLength} key={item.msg} />
        ))}
      </ul>
    </StyledNav>
  );
};

export default DesktopUtilityMenu;

ListItem.propTypes = {
  item: shape({
    hasDropDown: bool,
    isLogin: bool,
  }),
  itemNdx: number,
  itemsLength: number,
  dropDownItems: arrayOf(object),
  children: node,
};
